import React from 'react';
import { Box, Typography, Paper, CircularProgress, Alert, Avatar } from '@mui/material';
import RepeatIcon from '@mui/icons-material/Repeat';
import { useEvents } from '../contexts/EventsContext';
import { useUser } from '../contexts/UserContext';
import { useSchools } from '../contexts/SchoolsContext';
import { Bar } from 'react-chartjs-2';
import { Chart, CategoryScale, LinearScale, BarElement, Title, Tooltip as ChartTooltip, Legend } from 'chart.js';
import LinkPreview from '../components/LinkPreview';

// Helper to ensure website URLs have proper protocol
const formatWebsiteUrl = (url: string) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return `https://${url}`;
};

// Helper to format date based on when it was posted
const formatDate = (dateStr: string) => {
  const dt = new Date(dateStr);
  if (isNaN(dt.getTime())) return '';
  
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const postDate = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
  
  // Format time as h:mm a/p
  let hour = dt.getHours();
  const min = dt.getMinutes();
  const ampm = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12;
  if (hour === 0) hour = 12;
  const timeStr = `${hour}:${min.toString().padStart(2, '0')} ${ampm}`;
  
  // Check if posted today
  if (postDate.getTime() === today.getTime()) {
    return timeStr;
  }
  
  // Check if posted yesterday
  if (postDate.getTime() === yesterday.getTime()) {
    return `Yesterday, ${timeStr}`;
  }
  
  // Posted before yesterday - show date in m/d/yy format
  const m = dt.getMonth() + 1;
  const d = dt.getDate();
  const y = dt.getFullYear().toString().slice(-2);
  return `${m}/${d}/${y}`;
};

// Helper to format date only in m/d/yyyy
const formatDateOnly = (date: string) => {
  const dt = new Date(date);
  if (isNaN(dt.getTime())) return '';
  const m = dt.getMonth() + 1;
  const d = dt.getDate();
  const y = dt.getFullYear();
  return `${m}/${d}/${y}`;
};

// Helper to format time only in h:mm a/p
const formatTimeOnly = (date: string) => {
  const dt = new Date(date);
  if (isNaN(dt.getTime())) return '';
  let hour = dt.getHours();
  const min = dt.getMinutes();
  const ampm = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12;
  if (hour === 0) hour = 12;
  return `${hour}:${min.toString().padStart(2, '0')} ${ampm}`;
};

// Helper to format date and time from 'YYYY-MM-DD HH:mm' or 'YYYY-MM-DDTHH:mm' string
function formatDateTimeString(dateTimeStr: string) {
  const match = dateTimeStr && dateTimeStr.match(/(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  if (match) {
    const [, year, month, day, hour, minute] = match;
    const m = parseInt(month, 10);
    const d = parseInt(day, 10);
    const y = parseInt(year, 10);
    let hourNum = parseInt(hour, 10);
    const minNum = parseInt(minute, 10);
    const ampm = hourNum >= 12 ? 'PM' : 'AM';
    let displayHour = hourNum % 12;
    if (displayHour === 0) displayHour = 12;
    return {
      date: `${m}/${d}/${y}`,
      time: `${displayHour}:${minNum.toString().padStart(2, '0')} ${ampm}`
    };
  }
  return { date: '', time: '' };
}

const NewsfeedList: React.FC = () => {
  const { events, repostedEventsByCurrentUser, loading, error, refresh } = useEvents();
  const { user } = useUser();
  const { schools } = useSchools();

  // Get events where current user's high school is the host
  const hostedEvents = events.filter(event => 
    (event as any).hostName === user?.schoolName
  );

  // Combine reposted events and hosted events, removing duplicates
  const allNewsfeedEvents = [...repostedEventsByCurrentUser];
  
  // Add hosted events that aren't already in reposted events
  hostedEvents.forEach(hostedEvent => {
    const isAlreadyIncluded = repostedEventsByCurrentUser.some(
      repostedEvent => repostedEvent.id === hostedEvent.id
    );
    if (!isAlreadyIncluded) {
      allNewsfeedEvents.push(hostedEvent);
    }
  });

  // Map all newsfeed events to newsfeed format
  const newsfeedItems = allNewsfeedEvents
    .map(event => {
      // Parse start time for date/time display
      let startDateTime = '';
      let endDateTime = '';
      let postedDate = '';
      
      if ((event as any).startTime) {
        const { date, time } = formatDateTimeString((event as any).startTime);
        startDateTime = `${date} ${time}`;
      }

      if ((event as any).endTime) {
        const { date, time } = formatDateTimeString((event as any).endTime);
        endDateTime = `${date} ${time}`;
      }

      // Use repostedAt timestamp if available, otherwise use postTime
      if (event.repostedAt) {
        postedDate = new Date(event.repostedAt).toISOString();
      } else if ((event as any).postTime) {
        postedDate = new Date((event as any).postTime).toISOString();
      }

      // If neither repostedAt nor postTime, exclude this event
      if (!postedDate) return null;

      // Get school avatar/logo - same logic as Events List
      let schoolAvatar = '';
      const hostName = (event as any).hostName;
      
      if (schools.length > 0) {
        const school = schools.find(s => s.name === hostName);
        if (school) {
          // Use feedIcon first, fallback to resultsImage
          schoolAvatar = school.feedIcon || school.resultsImage || '';
        }
      }

      // Determine if this is a reposted event or hosted event
      const isReposted = event.repostedBy && typeof event.repostedBy === 'object' && user?.schoolID && Object.prototype.hasOwnProperty.call(event.repostedBy, user.schoolID);
      const isHostedByCurrentUser = hostName === user?.schoolName;
      // Determine postType based on eventType and repost status
      let postType = 'Event';
      const eventType = (event as any).eventType;
      if (eventType === 'news') {
        postType = 'News';
      } else if (isReposted) {
        postType = 'Reposted Event';
      } else if (eventType === 'physical') {
        postType = 'In-person Event';
      } else if (eventType === 'virtual') {
        postType = 'Virtual Event';
      } else if (eventType === 'poll') {
        postType = 'Poll';
      }

      return {
        id: event.id,
        postedBy: hostName || 'Unknown School',
        title: (event as any).title || 'Untitled Event',
        description: (event as any).description || '',
        postType: postType,
        postedDate: postedDate,
        startDateTime: startDateTime,
        endDateTime: endDateTime,
        location: (event as any).address || '',
        schoolAvatar: schoolAvatar,
        repostedAt: event.repostedAt,
        isReposted: isReposted,
        isHostedByCurrentUser: isHostedByCurrentUser,
        registerRequired: (event as any).registerRequired || false,
        website: (event as any).website || '',
        pollOptions: (event as any).pollOptions || [],
        pollResult: (event as any).pollResult || {},
        pollEndTimestamp: (event as any).pollEndTimestamp || '',
      };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);

  // Sort newsfeed items by postedDate descending (most recent first)
  const sortedNewsfeedItems = [...newsfeedItems].sort((a, b) => 
    new Date(b.postedDate).getTime() - new Date(a.postedDate).getTime()
  );

  // Filter to only show events from the past 6 months
  const sixMonthsAgo = new Date();
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
  const filteredNewsfeedItems = sortedNewsfeedItems.filter(item => {
    const postDate = new Date(item.postedDate);
    return !isNaN(postDate.getTime()) && postDate >= sixMonthsAgo;
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        The news and events below have been displayed for all active {user?.schoolName || 'High School'} users in the past 6 months.
      </Typography>
      {filteredNewsfeedItems.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }} elevation={2}>
          <Typography variant="h6" color="text.secondary">
            No events found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Events that you host or repost will appear here.
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {filteredNewsfeedItems.map((item) => (
            <Paper key={item.id} sx={{ p: 3, width: '100%' }} elevation={2}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar 
                    sx={{ 
                      width: 45, 
                      height: 45,
                      bgcolor: 'grey.300'
                    }}
                  >
                    {item.schoolAvatar ? (
                      <img 
                        src={item.schoolAvatar} 
                        alt={`${item.postedBy} logo`}
                        style={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'cover',
                          borderRadius: '50%'
                        }}
                        onError={(e) => {
                          // Fallback to user's school avatar if image fails to load
                                          if (item.isHostedByCurrentUser && user?.userIcon) {
                  (e.target as HTMLImageElement).src = user.userIcon;
                }
                        }}
                      />
                    ) : (
                      // Fallback to user's school avatar for hosted events
                                              item.isHostedByCurrentUser && user?.userIcon ? (
                          <img
                            src={user.userIcon} 
                          alt={`${item.postedBy} logo`}
                          style={{ 
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'cover',
                            borderRadius: '50%'
                          }}
                        />
                      ) : (
                        item.postedBy.charAt(0).toUpperCase()
                      )
                    )}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" color="text.primary">
                      {item.postedBy}
                    </Typography>
                    {/* Show reposted by if not hosted by current user's high school and is reposted */}
                    {item.isReposted && !item.isHostedByCurrentUser && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.2 }}>
                        <RepeatIcon sx={{ fontSize: 18, color: 'grey.500', mr: 0.5 }} />
                        <Typography variant="subtitle2" sx={{ color: 'grey.500', fontSize: '0.95rem' }}>
                          reposted by {user?.schoolName}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {formatDate(item.postedDate)}
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                {item.title}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {item.description}
              </Typography>
              {/* Poll Results Visualization */}
              {item.postType === 'Poll' && (item.pollOptions && item.pollResult && item.pollEndTimestamp) && (
                <Box sx={{ mb: 2 }}>
                  {(() => {
                    const options: string[] = item.pollOptions as string[];
                    // Convert pollResult values to numbers in case they are stored as strings
                    const numericResults: Record<string, number> = {};
                    Object.keys(item.pollResult).forEach((key) => {
                      const val = item.pollResult[key];
                      numericResults[key] = typeof val === 'number' ? val : Number(val) || 0;
                    });
                    // Debug log
                    console.log('PollResult raw:', item.pollResult);
                    console.log('PollResult numeric:', numericResults);
                    const totalVotes = Object.values(numericResults).reduce((sum: number, v: number) => sum + v, 0);
                    // Calculate percentages for each option
                    const percentages = options.map((_, idx) => {
                      const count = numericResults[String(idx)] || 0;
                      return totalVotes > 0 ? (count / totalVotes) * 100 : 0;
                    });
                    // Time remaining or final results
                    const now = Date.now();
                    const end = Number(item.pollEndTimestamp);
                    let timeNote = 'Final Results';
                    if (end > now) {
                      const ms = end - now;
                      const min = Math.floor(ms / 60000);
                      const hr = Math.floor(ms / 3600000);
                      const day = Math.floor(ms / 86400000);
                      if (day >= 1) timeNote = `${day} day${day > 1 ? 's' : ''} remaining`;
                      else if (hr >= 1) timeNote = `${hr} hour${hr > 1 ? 's' : ''} remaining`;
                      else timeNote = `${min} minute${min !== 1 ? 's' : ''} remaining`;
                    }
                    return (
                      <React.Fragment>
                        <Box sx={{ mb: 1 }}>
                          {options.map((option, idx) => (
                            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <Box sx={{ flex: 1, position: 'relative', minWidth: 0 }}>
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    left: 0,
                                    top: 0,
                                    height: 36,
                                    width: `${percentages[idx]}%`,
                                    bgcolor: percentages[idx] > 0 ? '#7ed957' : 'grey.100',
                                    borderRadius: 18,
                                    transition: 'width 0.5s',
                                    zIndex: 1,
                                  }}
                                />
                                <Box
                                  sx={{
                                    position: 'relative',
                                    display: 'flex',
                                    alignItems: 'center',
                                    height: 36,
                                    pl: 2,
                                    pr: 2,
                                    borderRadius: 18,
                                    fontWeight: (numericResults[String(idx)] || 0) > 0 ? 600 : 400,
                                    color: percentages[idx] > 0 ? 'black' : 'text.primary',
                                    zIndex: 2,
                                  }}
                                >
                                  {option}
                                </Box>
                              </Box>
                              <Box sx={{ minWidth: 60, textAlign: 'right', fontWeight: 700, fontSize: '1.1rem', ml: 2 }}>
                                {percentages[idx].toFixed(1)}%
                              </Box>
                            </Box>
                          ))}
                        </Box>
                        <Typography variant="caption" sx={{ display: 'block', textAlign: 'left', color: 'text.secondary', mt: 1 }}>
                          {`${totalVotes} vote${totalVotes !== 1 ? 's' : ''} • ${timeNote}`}
                        </Typography>
                      </React.Fragment>
                    );
                  })()}
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  {item.startDateTime && (
                    <Typography variant="caption" component="div">
                      {item.startDateTime}
                    </Typography>
                  )}
                  {item.endDateTime && (
                    <Typography variant="caption" component="div">
                      {item.endDateTime}
                    </Typography>
                  )}
                  {item.location && (
                    <Typography variant="caption" component="div">
                      {item.location}
                    </Typography>
                  )}
                  {/* Register note if required */}
                  {item.registerRequired && (
                    <Typography variant="caption" component="div" sx={{ color: 'primary.main', mt: 1 }}>
                      Register at the link below
                    </Typography>
                  )}
                  {/* Website preview image if website is populated */}
                  {item.website && (
                    <LinkPreview url={formatWebsiteUrl(item.website)} />
                  )}
                </Box>
                <Typography 
                  variant="caption"
                  color={item.postType === 'Reposted Event' ? 'text.secondary' : 'primary'}
                  sx={{ textAlign: 'right' }}
                >
                  {item.postType}
                </Typography>
              </Box>
            </Paper>
          ))}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 4, textAlign: 'center', fontStyle: 'italic' }}>
            *Events displayed are limited to the past 6 months.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default NewsfeedList; 